// Admin → Creative Performance tab (master-only, like the whole Admin page).
//
// Per-brand configuration for the Creative Performance Dashboard: enable the
// feature, map the brand to an analytics account (dropdown fed by the shared
// admin key — the raw key never reaches the browser), and pick a default tier.
// Brands without an enabled config show no sidebar link and the proxy returns
// a clear "not configured" error.

import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { LeadBackfillDialog } from "@/components/lead-backfill-dialog";
import { leadRecoveryRequest } from "@/lib/lead-recovery-request";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Brand } from "@shared/schema";
import {
  CREATIVE_TIERS,
  LEAD_SOURCE_FIELDS,
  DEFAULT_LEAD_SOURCE_FIELD,
  type CreativeAccount,
  type CreativeTier,
  type LeadSourceField,
  type CreativeFunnelResponse,
  type CreativeFunnelStepKey,
  KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID,
  KAYA_PRIMARY_CONVERSION_STEP,
} from "@shared/creative-performance";
import type { PerformanceBriefTemplate } from "@shared/ads-manager";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { SearchableSelect } from "@/lib/picker-search";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, TrendingUp, PlaneTakeoff } from "lucide-react";
import { LaunchConfigSection, type MetaPage } from "@/components/launch-config-section";

type CpConfig = {
  enabled: boolean;
  keySecretRef: string | null;
  accountId: string | null;
  defaultTier: CreativeTier;
  comparisonEnabled: boolean;
  dateComparisonEnabled: boolean;
  leadGenEnabled: boolean;
  leadSourceField: LeadSourceField;
  leadEventName: string | null;
  primaryConversionStep: CreativeFunnelStepKey | null;
  keyConfigured: boolean;
  adminKeyConfigured: boolean;
  funnelKeyConfigured: boolean;
  performanceBrief: boolean;
  performanceBriefTemplate: PerformanceBriefTemplate;
  performanceBriefAccountState: "connected" | "missing" | "unconfigured";
  shopifyReportingEnabled: boolean;
  shopifyAccountId: string | null;
  squareReportingEnabled: boolean;
  squareAccountId: string | null;
  squareLocationIds: string[];
  configUpdatedAt: string | null;
};
type ShopifyAccount = { accountId: string; accountName: string };
type SquareLocation = { locationId: string; merchantId: string; locationName: string; currency: string; timezone: string };
type SquareAccount = { accountId: string; accountName: string; locations: SquareLocation[] };

type PaginatedBrandsResp = { brands: Brand[]; total: number; totalPages: number; page: number };

export function ConfigDialog({ brand, onClose }: { brand: Brand; onClose: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const reportingQueryClient = useQueryClient();
  const initialized = useRef(false);
  const backfillTrigger = useRef<HTMLButtonElement>(null);
  const detailRecoveryTrigger = useRef<HTMLButtonElement>(null);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [maintenanceOperation, setMaintenanceOperation] = useState<"account" | "campaign_detail">("account");
  const { data: cfg, isLoading, error: configError, isFetching: configFetching, refetch: refetchConfig } = useQuery<CpConfig>({
    queryKey: [`/api/brands/${brand.id}/creative-performance/config`],
  });
  const [enabled, setEnabled] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [defaultTier, setDefaultTier] = useState<CreativeTier>("Cold");
  // Persona-first view mode (Task #2254): every brand defaults to the
  // Persona-only dashboard; this opt-in additionally shows the top-level
  // producer/brand comparison section.
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [dateComparisonEnabled, setDateComparisonEnabled] = useState(false);
  // Lead-gen scorecard mode (Task 2363): swaps the 30-second verdict for an
  // Ad Spend / Leads / CPL scorecard on the brand's dashboard.
  const [leadGenEnabled, setLeadGenEnabled] = useState(false);
  const [leadSourceField, setLeadSourceField] = useState<LeadSourceField>(DEFAULT_LEAD_SOURCE_FIELD);
  const [leadEventName, setLeadEventName] = useState("");
  // Account-specific conversion funnel (Task #2525): the primary conversion
  // step id. Null keeps the legacy dashboard exactly; a step makes the account
  // funnel authoritative. The selector below is tied to the SAVED mapped
  // account only.
  const [primaryConversionStep, setPrimaryConversionStep] = useState<CreativeFunnelStepKey | null>(null);
  // Task #2155 — Ad Launcher brand flag now lives here (moved from the Brand
  // Features tab). It is still the plain `enableAdLauncher` brand setting,
  // saved via the same PATCH /api/brands/:id mechanism that tab used.
  const [adLauncher, setAdLauncher] = useState<boolean>(
    !!(brand.settings as any)?.enableAdLauncher,
  );
  const [performanceBrief, setPerformanceBrief] = useState<boolean>(
    !!(brand.settings as any)?.enableMetaAds,
  );
  const [performanceBriefTemplate, setPerformanceBriefTemplate] = useState<PerformanceBriefTemplate>("ecommerce");
  const [shopifyReportingEnabled, setShopifyReportingEnabled] = useState(false);
  const [shopifyAccountId, setShopifyAccountId] = useState("");
  const [squareReportingEnabled, setSquareReportingEnabled] = useState(false);
  const [squareAccountId, setSquareAccountId] = useState("");
  const [squareLocationIds, setSquareLocationIds] = useState<string[]>([]);
  // When the launcher is toggled ON at save time (with a mapped account), the
  // dialog steps straight into launch setup: LaunchConfigSection auto-runs the
  // read-only Windsor detection and pre-fills the config so the admin reviews
  // detected values immediately instead of finding blank fields later.
  const [launchSetup, setLaunchSetup] = useState<{ accountId: string; accountName?: string } | null>(null);

  // Accounts the shared admin key can see (dropdown source).
  const { data: accounts, error: accountsError } = useQuery<CreativeAccount[]>({
    queryKey: ["/api/admin/creative-performance/accounts"],
    retry: false,
  });
  const shopifyAccountsQ = useQuery<ShopifyAccount[]>({
    queryKey: ["/api/admin/creative-performance/shopify-accounts"],
    retry: false,
  });
  const squareAccountsQ = useQuery<SquareAccount[]>({
    queryKey: ["/api/admin/creative-performance/square-accounts"],
    enabled: squareReportingEnabled,
    retry: false,
  });
  // The post-enable launch setup also uses the shared config form. Keep the
  // Meta pages lookup in this always-mounted dialog owner so the form receives
  // the same named page choices as the regular Ad Launcher configuration.
  const metaConnectionQ = useQuery<{ status: string } | null>({
    queryKey: [`/api/brands/${brand.id}/meta-ads/connection`],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brand.id}/meta-ads/connection`, { credentials: "include" });
      if (res.status === 204 || !res.ok) return null;
      return res.json();
    },
    enabled: !!launchSetup,
    retry: false,
  });
  const metaPagesQ = useQuery<MetaPage[]>({
    queryKey: [`/api/brands/${brand.id}/meta-ads/pages`],
    enabled: !!launchSetup && metaConnectionQ.data?.status === "connected",
    retry: false,
  });

  useEffect(() => {
    // Refetches after a backfill must not overwrite unrelated unsaved edits.
    if (cfg && !configFetching && !initialized.current) {
      initialized.current = true;
      setEnabled(cfg.enabled);
      setAccountId(cfg.accountId || "");
      setDefaultTier(cfg.defaultTier);
      setComparisonEnabled(cfg.comparisonEnabled === true);
      setDateComparisonEnabled(cfg.dateComparisonEnabled === true);
      setLeadGenEnabled(cfg.leadGenEnabled === true);
      const kayaReservationsSelected =
        brand.name.trim().toLowerCase() === "kaya"
        && cfg.accountId === KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID
        && cfg.primaryConversionStep === KAYA_PRIMARY_CONVERSION_STEP;
      setLeadSourceField(kayaReservationsSelected ? "leads" : (cfg.leadSourceField || DEFAULT_LEAD_SOURCE_FIELD));
      setLeadEventName(kayaReservationsSelected ? KAYA_PRIMARY_CONVERSION_STEP : (cfg.leadEventName || ""));
      if (kayaReservationsSelected) setLeadGenEnabled(true);
      setPrimaryConversionStep(cfg.primaryConversionStep ?? null);
      setPerformanceBrief(cfg.performanceBrief === true);
      setPerformanceBriefTemplate(cfg.performanceBriefTemplate);
      setShopifyReportingEnabled(cfg.shopifyReportingEnabled === true);
      setShopifyAccountId(cfg.shopifyAccountId || "");
       setSquareReportingEnabled(cfg.squareReportingEnabled === true);
       setSquareAccountId(cfg.squareAccountId || "");
       setSquareLocationIds(Array.isArray(cfg.squareLocationIds) ? cfg.squareLocationIds : []);
    }
  }, [cfg, configFetching]);

  // Kaya-only custom-conversion metadata. The browser never chooses a client
  // or account: the brand-scoped route injects client=kaya server-side. Query
  // only after the Kaya mapping is saved and remains unchanged in this dialog.
  const isKaya = brand.name.trim().toLowerCase() === "kaya";
  const savedAccountId = cfg?.accountId || "";
  const savedKayaReservations = isKaya
    && savedAccountId === KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID
    && cfg?.primaryConversionStep === KAYA_PRIMARY_CONVERSION_STEP;
  const savedLeadBrief = cfg?.performanceBrief === true && cfg.performanceBriefTemplate === "lead_gen";
  const reportingSettings = useQuery<{
    accountSetup?: string;
    performanceBriefTemplate?: PerformanceBriefTemplate;
    accounts?: { id: string; name: string }[];
  }>({
    queryKey: ["/api/brands", brand.id, "ads-performance", "settings"],
    queryFn: async () => (await apiRequest("GET", `/api/brands/${brand.id}/ads-performance/settings`)).json(),
    enabled: user?.isMaster === true && savedLeadBrief,
    retry: false,
    staleTime: 0,
  });
  // Resolve from authorized persisted reporting settings, never the account picker.
  const backfillAccountId = reportingSettings.data?.accounts?.[0]?.id || "";
  const relevantEdits = !!cfg && (
    accountId !== savedAccountId
    || performanceBrief !== cfg.performanceBrief
    || performanceBriefTemplate !== cfg.performanceBriefTemplate
    || leadGenEnabled !== (savedKayaReservations || cfg.leadGenEnabled)
    || leadEventName.trim() !== (savedKayaReservations ? KAYA_PRIMARY_CONVERSION_STEP : (cfg.leadEventName || ""))
    || leadSourceField !== (savedKayaReservations ? "leads" : cfg.leadSourceField)
    || primaryConversionStep !== (cfg.primaryConversionStep ?? null)
  );
  const accountMatches = !!backfillAccountId && backfillAccountId === savedAccountId.replace(/^act_/i, "");
  const backfillContext = useQuery({
    queryKey: [`/api/brands/${brand.id}/ads-performance/lead-backfill/context?accountId=${encodeURIComponent(backfillAccountId)}`],
    queryFn: async ({ queryKey }) => leadRecoveryRequest(queryKey[0]),
    enabled: user?.isMaster === true && savedLeadBrief && accountMatches
      && reportingSettings.data?.accountSetup === "connected",
    retry: false,
    staleTime: 0,
  });
  useEffect(() => {
    setBackfillOpen(false);
  }, [brand.id, backfillAccountId]);
  const backfillUnavailable = configError ? "Saved Analytics configuration could not be loaded. Retry before backfilling."
    : isLoading || configFetching || !initialized.current ? "Loading saved Analytics configuration…"
    : relevantEdits ? "Save the account, Performance Brief template, and lead event changes before backfilling."
    : !savedLeadBrief ? "Save an enabled lead-gen Performance Brief configuration first."
    : reportingSettings.isLoading || reportingSettings.isFetching ? "Loading the saved authorized reporting account…"
    : reportingSettings.error ? `Reporting settings unavailable: ${(reportingSettings.error as Error).message}`
    : !backfillAccountId ? "Save and connect a Meta reporting account first."
    : !accountMatches || reportingSettings.data?.accountSetup !== "connected"
      || reportingSettings.data?.performanceBriefTemplate !== "lead_gen" ? "Save to verify the reporting account and lead-gen configuration first."
    : backfillContext.isLoading || backfillContext.isFetching ? "Checking the saved event and provider timezone…"
    : backfillContext.error ? `Backfill unavailable: ${(backfillContext.error as Error).message}`
     : !backfillContext.data ? "Recovery prerequisites have not been verified. Retry the saved event and provider timezone check."
    : "";
  const refreshAfterBackfill = async () => {
    await Promise.allSettled([
      reportingQueryClient.invalidateQueries({ queryKey: ["/api/brands", brand.id, "ads-performance"] }),
      reportingQueryClient.invalidateQueries({ queryKey: ["/api/brands", brand.id, "ads-manager"] }),
      reportingQueryClient.invalidateQueries({ queryKey: [`/api/brands/${encodeURIComponent(brand.id)}/ads-performance/lead-detail-recovery`] }),
      reportingQueryClient.invalidateQueries({ queryKey: [`/api/brands/${brand.id}/creative-performance/config`] }),
      reportingQueryClient.invalidateQueries({ queryKey: ["/api/brands", brand.id] }),
    ]);
  };
  const editedMatchesSaved =
    isKaya
    && savedAccountId === KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID
    && accountId === savedAccountId;
  const kayaReservationsSelected =
    isKaya
    && accountId === KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID
    && primaryConversionStep === KAYA_PRIMARY_CONVERSION_STEP;
  const funnelMetaSince = (() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();
  const funnelMetaUntil = new Date().toISOString().slice(0, 10);
  const funnelMetaQs = `since=${funnelMetaSince}&until=${funnelMetaUntil}`;
  const funnelMeta = useQuery<CreativeFunnelResponse>({
    queryKey: [`/api/brands/${brand.id}/creative-performance/funnel?${funnelMetaQs}`],
    enabled: editedMatchesSaved && cfg?.funnelKeyConfigured !== false,
    retry: false,
  });

  const save = useMutation({
    mutationFn: async () => {
      // One request persists BOTH the analytics config and the Ad Launcher
      // flag: the server merges them into brands.settings in a single write.
      // (A previous version followed this PUT with a whole-settings brand
      // PATCH built from the stale `brand` prop, which clobbered the account
      // mapping just written and made launcher detection 409.)
      const res = await apiRequest("PUT", `/api/brands/${brand.id}/creative-performance/config`, {
        enabled,
        accountId: accountId || null,
        defaultTier,
        comparisonEnabled,
        dateComparisonEnabled,
        leadGenEnabled,
        leadSourceField,
        leadEventName: leadEventName.trim() || null,
        primaryConversionStep,
        adLauncher,
        performanceBrief,
        performanceBriefTemplate,
        shopifyReportingEnabled,
        shopifyAccountId: shopifyReportingEnabled ? shopifyAccountId || null : null,
         squareReportingEnabled,
         squareAccountId: squareReportingEnabled ? squareAccountId || null : null,
         squareLocationIds: squareReportingEnabled ? squareLocationIds : [],
        configUpdatedAt: cfg?.configUpdatedAt,
      });
      return (await res.json()) as CpConfig;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brand.id}/creative-performance/config`] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brand.id] });
      queryClient.invalidateQueries({
        predicate: (q) => typeof q.queryKey[0] === "string" && (q.queryKey[0] as string).startsWith("/api/brands"),
      });
      toast({
        title: "Analytics config saved",
        description: data.performanceBrief
          ? `${brand.name}: selected account is ready for Creative Performance, Performance Brief, and Ads Manager.`
          : data.enabled ? `${brand.name}: Creative Performance settings saved; Performance Brief is off.` : `${brand.name}: disabled.`,
      });
      // Whenever launcher access is enabled and an account is mapped, continue
      // into launch setup instead of closing. This also covers an already-on
      // launcher whose analytics mapping was added or changed.
      const mappedAccount = data.accountId || accountId;
      if (adLauncher && mappedAccount) {
        const match = (accounts ?? []).find((a) => a.accountId === mappedAccount);
        setLaunchSetup({ accountId: mappedAccount, accountName: match?.accountName });
        return;
      }
      onClose();
    },
    onError: (e: Error) => {
      if (/updated by another admin/i.test(e.message)) {
        queryClient.invalidateQueries({ queryKey: [`/api/brands/${brand.id}/creative-performance/config`] });
      }
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className={launchSetup
          ? "w-[calc(100vw-1rem)] max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto"
          : "w-[calc(100vw-1rem)] max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto"}
        data-testid="dialog-cp-config"
      >
        <DialogHeader>
          <DialogTitle className="text-sm">
            {launchSetup ? `Ad Launcher setup — ${brand.name}` : `Analytics — ${brand.name}`}
          </DialogTitle>
        </DialogHeader>
        {launchSetup ? (
          // Step 2 (launcher just enabled): the launch config auto-runs
          // read-only detection from the account's recent campaigns and
          // pre-fills the form for review. Nothing saves without the admin.
          <div className="space-y-3" data-testid="section-launch-setup-step">
            <LaunchConfigSection
              brandId={brand.id}
              accountId={launchSetup.accountId}
              accountName={launchSetup.accountName}
              metaPages={metaPagesQ.data ?? []}
            />
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="button-launch-setup-done">Done</Button>
            </DialogFooter>
          </div>
         ) : isLoading ? (
          <Skeleton className="h-40 w-full" />
         ) : configError || !cfg ? (
           <div role="alert" className="space-y-2 text-sm">
             Saved Analytics configuration could not be loaded.
             <Button variant="outline" onClick={() => void refetchConfig()}>Retry configuration</Button>
           </div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center justify-between text-sm">
              Enable dashboard for this brand
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="switch-cp-enabled" />
            </label>
            <div className="space-y-1.5">
              <Label className="text-xs">Analytics account (shared admin key)</Label>
              {accountsError ? (
                <p className="text-[11px] text-muted-foreground">
                  Account list unavailable: {(accountsError as Error).message}. The <code>CREATIVE_API_KEY_ADMIN</code> secret
                  must be set on the server.
                </p>
              ) : (
                <SearchableSelect
                  value={accountId || "__none__"}
                   onValueChange={(v) => {
                     const nextAccountId = v === "__none__" ? "" : v;
                      if (nextAccountId !== accountId) {
                        if (primaryConversionStep === KAYA_PRIMARY_CONVERSION_STEP) {
                          setLeadGenEnabled(false);
                          setLeadSourceField(DEFAULT_LEAD_SOURCE_FIELD);
                          setLeadEventName("");
                        }
                        setPrimaryConversionStep(null);
                      }
                     setAccountId(nextAccountId);
                   }}
                  testId="select-cp-account"
                  options={[
                    { value: "__none__", label: "None" },
                    ...(accounts ?? []).map((a) => ({
                      value: a.accountId,
                      label: a.lastDate ? `${a.accountName} (data through ${a.lastDate})` : a.accountName,
                    })),
                  ]}
                />
              )}
              <p className="text-[11px] text-muted-foreground">
                Pick which analytics account this brand maps to. The shared admin key is used server-side.
              </p>
              {cfg?.keySecretRef && !accountId && (
                <p className="text-[11px] text-amber-600">
                  This brand currently uses a legacy per-brand key ({cfg.keySecretRef}); selecting an account switches it to the shared admin key.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Default tier</Label>
              <SearchableSelect
                value={defaultTier}
                onValueChange={(v) => setDefaultTier(v as CreativeTier)}
                testId="select-cp-default-tier"
                options={CREATIVE_TIERS.map((t) => ({ value: t, label: t }))}
              />
            </div>
            <div className="space-y-1.5 border-t pt-3">
              <label className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5">
                  <TrendingUp className="h-3.5 w-3.5 shrink-0" /> Performance Brief
                </span>
                <Switch
                  checked={performanceBrief}
                  onCheckedChange={setPerformanceBrief}
                  data-testid="switch-cp-performance-brief"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                 Show Ads Manager and Performance Brief for this brand. Lead-gen history can be reviewed and backfilled here after saving.
              </p>
              <div className="space-y-1.5">
                <label className="text-sm">Performance Brief template</label>
                <SearchableSelect
                  value={performanceBriefTemplate}
                  onValueChange={(value) => setPerformanceBriefTemplate(value as PerformanceBriefTemplate)}
                  testId="select-cp-performance-brief-template"
                  options={[
                    { value: "ecommerce", label: "Ecommerce" },
                    { value: "lead_gen", label: "Lead gen" },
                  ]}
                />
                <p className="text-[11px] text-muted-foreground">Choose the report template for this brand. Both options currently show the same report.</p>
              </div>
              {performanceBrief && <p className="text-[11px] text-amber-700" data-testid="text-cp-brief-account-state">
                {accountId !== (cfg?.accountId || "") ? "Save to verify the new account with Analytics and Windsor. An archived or another brand's Windsor connection cannot be reused." :
                 cfg?.performanceBriefAccountState === "connected" ? "Connected: the selected Analytics account has an active Windsor reporting connection. Recovery readiness is checked separately below." :
                 cfg?.performanceBriefAccountState === "missing" ? "Select a Meta ad account and save to set up reporting." :
                 "Setup needed: save to verify and connect this account for Windsor reporting. If verification fails, check the Windsor connection and retry."}
              </p>}
              {user?.isMaster === true && (savedLeadBrief || performanceBriefTemplate === "lead_gen") && (
                <div className="space-y-1.5 pt-2">
                  <div className="flex flex-wrap gap-2">
                    <Button ref={backfillTrigger} type="button" size="sm" variant="outline"
                      disabled={!!backfillUnavailable || save.isPending} onClick={() => {
                        setMaintenanceOperation("account"); setBackfillOpen(true);
                      }}
                      data-testid="button-cp-lead-backfill" aria-describedby="cp-backfill-help">
                      Backfill last 60 days
                    </Button>
                    <Button ref={detailRecoveryTrigger} type="button" size="sm" variant="outline"
                      disabled={!!backfillUnavailable || save.isPending} onClick={() => {
                        setMaintenanceOperation("campaign_detail"); setBackfillOpen(true);
                      }}
                      data-testid="button-cp-lead-detail-recovery" aria-describedby="cp-detail-recovery-help">
                      Review campaign lead recovery
                    </Button>
                  </div>
                  <p id="cp-backfill-help" className="text-[11px] text-muted-foreground">
                    {backfillUnavailable || "Review the saved account's last 60 completed days before explicitly confirming any writes."}
                  </p>
                  <p id="cp-detail-recovery-help" className="text-[11px] text-muted-foreground">
                    Account backfill saves account totals only. Campaign recovery can collect missing configured-event detail and authoritative objectives, including already-covered dates. If Meta still omits an objective, lead metrics remain unavailable.
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-1.5 border-t pt-3" data-testid="section-cp-shopify-reporting">
              <label className="flex items-center justify-between text-sm">
                Shopify reporting
                <Switch
                  checked={shopifyReportingEnabled}
                  onCheckedChange={setShopifyReportingEnabled}
                  data-testid="switch-cp-shopify-reporting"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                Include Shopify-attributed commerce metrics in this brand's Performance Brief.
              </p>
              {shopifyReportingEnabled && (
                shopifyAccountsQ.isLoading ? <Skeleton className="h-9 w-full" /> :
                shopifyAccountsQ.error ? (
                  <p className="text-[11px] text-amber-600" data-testid="text-cp-shopify-unavailable">
                    Shopify accounts are unavailable: {(shopifyAccountsQ.error as Error).message}
                  </p>
                ) : (
                  <>
                    <Label className="text-xs">Shopify account</Label>
                    <SearchableSelect
                      value={shopifyAccountId || "__none__"}
                      onValueChange={value => setShopifyAccountId(value === "__none__" ? "" : value)}
                      testId="select-cp-shopify-account"
                      options={[
                        { value: "__none__", label: "Select an account" },
                        ...(shopifyAccountsQ.data ?? []).map(account => ({
                          value: account.accountId,
                          label: account.accountName,
                        })),
                      ]}
                    />
                    {!shopifyAccountId && <p className="text-[11px] text-amber-600">Select one Shopify account to save.</p>}
                  </>
                )
              )}
            </div>
            <div className="space-y-1.5 border-t pt-3" data-testid="section-cp-square-reporting">
              <label className="flex items-center justify-between text-sm">
                Enable Square sales reporting
                <Switch
                  checked={squareReportingEnabled}
                  onCheckedChange={setSquareReportingEnabled}
                  data-testid="switch-cp-square-reporting"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                Add Square point-of-sale sales as a separate source in this brand's Performance Brief. Square sales are not attributed to Meta.
              </p>
              {squareReportingEnabled && (
                squareAccountsQ.isLoading ? <Skeleton className="h-9 w-full" /> :
                squareAccountsQ.error ? (
                  <p className="text-[11px] text-amber-600" role="alert" data-testid="text-cp-square-unavailable">
                    Square account catalog is unavailable: {(squareAccountsQ.error as Error).message}
                  </p>
                ) : (
                  <>
                    <Label className="text-xs">Square account</Label>
                    <SearchableSelect
                      value={squareAccountId || "__none__"}
                      onValueChange={value => {
                        const next = value === "__none__" ? "" : value;
                        if (next !== squareAccountId) setSquareLocationIds([]);
                        setSquareAccountId(next);
                      }}
                      testId="select-cp-square-account"
                      options={[
                        { value: "__none__", label: "Select an account" },
                        ...(squareAccountsQ.data ?? []).map(account => ({ value: account.accountId, label: account.accountName })),
                      ]}
                    />
                    {squareAccountId && (() => {
                      const ownedLocations = squareAccountsQ.data?.find(account => account.accountId === squareAccountId)?.locations ?? [];
                      const selectedLocationIsOwned = squareLocationIds.length === 1
                        && ownedLocations.some(location => location.locationId === squareLocationIds[0]);
                      return (
                        <div className="space-y-1.5 pt-1">
                          <Label className="text-xs">Square location</Label>
                          <SearchableSelect
                            value={selectedLocationIsOwned ? squareLocationIds[0] : "__none__"}
                            onValueChange={value => setSquareLocationIds(value === "__none__" ? [] : [value])}
                            testId="select-cp-square-location"
                            options={[
                              { value: "__none__", label: "Select a location" },
                              ...ownedLocations.map(location => ({
                                value: location.locationId,
                                label: `${location.locationName} · ${location.currency} · ${location.timezone}`,
                              })),
                            ]}
                          />
                          {!selectedLocationIsOwned && (
                            <p className="text-[11px] text-amber-600" data-testid="text-cp-square-location-required">
                              Select one location belonging to this Square account to save.
                            </p>
                          )}
                          {squareLocationIds.length > 0 && !selectedLocationIsOwned && (
                            <p className="text-[11px] text-destructive" role="alert" data-testid="text-cp-square-ownership">
                              The saved location is not owned by the selected Square account. Choose an account-owned location.
                            </p>
                          )}
                          {ownedLocations.length === 0 && (
                            <p className="text-[11px] text-amber-600">No locations are available for this Square account.</p>
                          )}
                        </div>
                      );
                    })()}
                    {!squareAccountId && <p className="text-[11px] text-amber-600" data-testid="text-cp-square-account-required">Select one Square account to save.</p>}
                  </>
                )
              )}
            </div>
            <div className="space-y-1.5 border-t pt-3">
              <label className="flex items-center justify-between text-sm">
                Producer comparison view
                <Switch
                  checked={comparisonEnabled}
                  onCheckedChange={setComparisonEnabled}
                  data-testid="switch-cp-comparison"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                All brands get the Persona-first dashboard. Turn this on to additionally show the
                top-level PersonaOS vs in-house comparison scorecard (comparison never extends into
                the quadrant, funnel, or trend sections).
              </p>
            </div>
            <div className="space-y-1.5 border-t pt-3">
              <label className="flex items-center justify-between text-sm">
                Previous-period lead comparison
                <Switch
                  checked={dateComparisonEnabled}
                  onCheckedChange={setDateComparisonEnabled}
                  data-testid="switch-cp-date-comparison"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                Let this brand compare its Lead performance scorecard with the immediately preceding equal-length date window. This is separate from producer comparison.
              </p>
            </div>
            <div className="space-y-1.5 border-t pt-3">
              <label className="flex items-center justify-between text-sm">
                Lead-gen scorecard
                <Switch
                  checked={kayaReservationsSelected || leadGenEnabled}
                  onCheckedChange={setLeadGenEnabled}
                  disabled={kayaReservationsSelected}
                  data-testid="switch-cp-lead-gen"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                For lead-gen clients: the dashboard's top scorecard shows Ad Spend, Leads, and CPL
                instead of the e-commerce 30-second verdict. Everything else stays the same.
              </p>
              {(kayaReservationsSelected || leadGenEnabled) && (
                <div className="space-y-1.5 pt-1">
                  <Label className="text-xs">Meta lead event (counted as leads)</Label>
                  {kayaReservationsSelected ? (
                    <>
                      <Input value={KAYA_PRIMARY_CONVERSION_STEP} readOnly data-testid="input-cp-lead-event" />
                      <p className="text-[11px] text-muted-foreground" data-testid="text-cp-kaya-lead-event">
                        Kaya Reservations always uses the server-owned completed OpenTable event. It cannot fall back to add-to-cart or another feed column.
                      </p>
                    </>
                  ) : (
                    <>
                      <Input
                        value={leadEventName}
                        onChange={(e) => setLeadEventName(e.target.value)}
                        placeholder="e.g. complete_registration, lead, offsite_conversion.custom.<id>"
                        data-testid="input-cp-lead-event"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Sent to the analytics feed per request, which counts this event into a
                        per-ad <code>leads</code> field — no hardcoding upstream. Leave blank to fall back
                        to a feed column instead:
                      </p>
                    </>
                  )}
                  {!kayaReservationsSelected && !leadEventName.trim() && (
                    <SearchableSelect
                      value={leadSourceField}
                      onValueChange={(v) => setLeadSourceField(v as LeadSourceField)}
                      testId="select-cp-lead-source"
                      options={LEAD_SOURCE_FIELDS.filter((f) => f !== "leads").map((f) => ({
                        value: f,
                        label: f === "checkout" ? "checkout (default — lead events ride this column)" : f,
                      }))}
                    />
                  )}
                </div>
              )}
            </div>
            {isKaya ? <div className="space-y-1.5 border-t pt-3" data-testid="section-cp-primary-step">
              <Label className="text-xs">Primary conversion step</Label>
              {(() => {
                // The funnel selector is tied to the SAVED mapped account. We
                // never pass an account id from the browser — the endpoint
                // resolves it server-side — so an edited-but-unsaved account
                // change is explained rather than queried.
                if (accountId !== KAYA_CUSTOM_CONVERSIONS_ACCOUNT_ID) {
                  return (
                    <p className="text-[11px] text-muted-foreground" data-testid="text-cp-step-no-account">
                      Map Kaya to its configured analytics account before enabling its reservation conversion.
                    </p>
                  );
                }
                if (!editedMatchesSaved) {
                  return (
                    <p className="text-[11px] text-amber-600" data-testid="text-cp-step-save-first">
                       Save Kaya's account mapping first — the custom conversion is read only from the saved Kaya configuration.
                    </p>
                  );
                }
                if (cfg?.funnelKeyConfigured === false) {
                  return (
                    <div className="space-y-2">
                      <p className="text-[11px] text-muted-foreground" data-testid="text-cp-step-key-missing">
                        Conversion steps cannot be loaded until the shared server-only analytics credential is configured.
                      </p>
                      {primaryConversionStep && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPrimaryConversionStep(null)}
                          data-testid="button-cp-use-legacy"
                        >
                          Use legacy dashboard
                        </Button>
                      )}
                    </div>
                  );
                }
                if (funnelMeta.isLoading) {
                  return <Skeleton className="h-9 w-full" data-testid="skeleton-cp-steps" />;
                }
                if (funnelMeta.error || !funnelMeta.data) {
                  return (
                    <div className="space-y-2">
                      <p className="text-[11px] text-muted-foreground" data-testid="text-cp-step-unavailable">
                        Conversion steps are unavailable for this account right now
                        {funnelMeta.error ? `: ${(funnelMeta.error as Error).message}` : ""}.
                      </p>
                      {primaryConversionStep && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setPrimaryConversionStep(null)}
                          data-testid="button-cp-use-legacy"
                        >
                          Use legacy dashboard
                        </Button>
                      )}
                    </div>
                  );
                }
                const { stepOrder, meanings } = funnelMeta.data;
                const options = [
                  { value: "__none__", label: "None (legacy dashboard)" },
                  ...stepOrder
                    .filter((k) => k === KAYA_PRIMARY_CONVERSION_STEP)
                    .map((k) => meanings[k])
                    .filter((m) => !!m)
                    .map((m) => ({
                      value: m.key,
                      label: `${m.label}${m.source === "mapping" ? " · mapped" : " · standard"}${m.known ? "" : " · unavailable"}`,
                    })),
                ];
                const current = primaryConversionStep ? meanings[primaryConversionStep] : null;
                return (
                  <>
                    <SearchableSelect
                      value={primaryConversionStep || "__none__"}
                       onValueChange={(v) => {
                         const next = v === "__none__" ? null : (v as CreativeFunnelStepKey);
                         setPrimaryConversionStep(next);
                         if (next === KAYA_PRIMARY_CONVERSION_STEP) {
                           setLeadGenEnabled(true);
                           setLeadSourceField("leads");
                           setLeadEventName(KAYA_PRIMARY_CONVERSION_STEP);
                         } else if (primaryConversionStep === KAYA_PRIMARY_CONVERSION_STEP) {
                           setLeadGenEnabled(false);
                           setLeadSourceField(DEFAULT_LEAD_SOURCE_FIELD);
                           setLeadEventName("");
                         }
                       }}
                      testId="select-cp-primary-step"
                      options={options}
                    />
                    {primaryConversionStep && (
                      <p className="text-[11px] flex items-center gap-1.5" data-testid="text-cp-step-provenance">
                        {current ? (
                          <>
                            <Badge
                              variant="secondary"
                              className={current.source === "mapping"
                                ? "bg-violet-500/15 text-violet-700 dark:text-violet-400 text-[10px]"
                                : "bg-muted text-muted-foreground text-[10px]"}
                              data-testid="badge-cp-step-source"
                            >
                              {current.source === "mapping" ? "Account mapping" : "Standard step"}
                            </Badge>
                            <span className={current.known ? "text-muted-foreground" : "text-amber-600"} data-testid="text-cp-step-tracked">
                              {current.known ? `Tracked as "${current.label}".` : `"${current.label}" is not currently tracked by this account.`}
                            </span>
                          </>
                        ) : (
                          <span className="text-amber-600" data-testid="text-cp-step-missing">
                            The saved step "{primaryConversionStep}" isn't in this account's current steps.
                          </span>
                        )}
                      </p>
                    )}
                    <p className="text-[11px] text-muted-foreground">
                      Uses Kaya's reservations for the dashboard scorecard. Reservation starts remain a separate supporting metric; pixel reservations are not combined.
                    </p>
                  </>
                );
              })()}
            </div> : (
              <div className="space-y-1.5 border-t pt-3" data-testid="section-cp-primary-step-unavailable">
                <Label className="text-xs">Primary conversion step</Label>
                <p className="text-[11px] text-muted-foreground">
                  Custom conversion reporting is currently enabled only for Kaya.
                </p>
              </div>
            )}
            <div className="space-y-1.5 border-t pt-3">
              <label className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1.5">
                  <PlaneTakeoff className="h-3.5 w-3.5 shrink-0" /> Ad Launcher
                </span>
                <Switch
                  checked={adLauncher}
                  onCheckedChange={setAdLauncher}
                  data-testid="switch-cp-ad-launcher"
                />
              </label>
              <p className="text-[11px] text-muted-foreground">
                Let non-master users of this brand launch Meta ads through the Windsor launcher.
                {!accountId && (
                  <span className="text-amber-600">
                    {" "}No analytics account is mapped — the launcher needs one, so its sidebar entry stays hidden until an account is selected above.
                  </span>
                )}
              </p>
              <p className="text-[11px] text-muted-foreground">
                Enabling both Ads Manager and Ad Launcher makes editable Duplicate drafts available
                to authorized editors in development and production. No extra duplication grant is
                needed. Imports never launch automatically or preserve original engagement.
              </p>
            </div>
            {accountId && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="text-launch-config-moved">
                <PlaneTakeoff className="h-3.5 w-3.5 shrink-0" />
                Launch settings (vertical, page, pixel, default template, naming) are configured from
                the brand's Ad Launcher page via its Config button.
              </p>
            )}
          </div>
        )}
        {!launchSetup && <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || isLoading || !!configError || !cfg || (shopifyReportingEnabled && (!shopifyAccountId || shopifyAccountsQ.isLoading || !!shopifyAccountsQ.error)) || (squareReportingEnabled && (squareAccountsQ.isLoading || !!squareAccountsQ.error || !squareAccountId || squareLocationIds.length !== 1 || !(squareAccountsQ.data?.find(a => a.accountId === squareAccountId)?.locations.some(l => l.locationId === squareLocationIds[0]))))} data-testid="button-cp-save">
            {save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save
          </Button>
        </DialogFooter>}
        {user?.isMaster === true && savedLeadBrief && <LeadBackfillDialog
          key={`${brand.id}:${backfillAccountId}:${maintenanceOperation}`}
          operation={maintenanceOperation}
          open={backfillOpen}
          onOpenChange={setBackfillOpen}
          brandId={brand.id}
          accountId={backfillAccountId}
          onCommitted={refreshAfterBackfill}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            (maintenanceOperation === "campaign_detail" ? detailRecoveryTrigger : backfillTrigger).current?.focus();
          }}
        />}
      </DialogContent>
    </Dialog>
  );
}

export function CreativePerformanceConfigTab() {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Brand | null>(null);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery<PaginatedBrandsResp>({
    queryKey: ["/api/admin/brands", { page, search: debounced, cp: true }],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page) });
      if (debounced) params.set("search", debounced);
      const res = await apiRequest("GET", `/api/admin/brands?${params.toString()}`);
      return res.json();
    },
    placeholderData: (prev) => prev,
  });

  // Account id → name map so the status badges show readable account names.
  const { data: accounts } = useQuery<CreativeAccount[]>({
    queryKey: ["/api/admin/creative-performance/accounts"],
    retry: false,
  });
  const accountName = (id: string) =>
    accounts?.find((a) => a.accountId === id)?.accountName || `account ${id}`;

  const brands = data?.brands ?? [];
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-medium mb-1 flex items-center gap-2" data-testid="text-cp-admin-title">
          <TrendingUp className="h-5 w-5" /> Analytics
        </h2>
        <p className="text-sm text-muted-foreground">
          Per-brand config for the external analytics dashboard (master-only feature). Map each brand to its
          analytics ad account; data is fetched server-side with the shared admin key.
        </p>
      </div>
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search brands…" className="max-w-xs" data-testid="input-cp-brand-search" />
      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <div className="rounded-md border" data-testid="table-cp-brands">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brand</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-32" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {brands.map((b) => {
                const cp = (b.settings as any)?.creativePerformance;
                return (
                  <TableRow key={b.id} data-testid={`row-cp-brand-${b.id}`}>
                    <TableCell className="font-medium">{b.name}</TableCell>
                    <TableCell>
                      {cp?.enabled ? (
                        <Badge variant="secondary" className="bg-green-500/15 text-green-700 dark:text-green-400">
                          Enabled · {cp.accountId ? accountName(cp.accountId) : cp.keySecretRef ? `legacy key (${cp.keySecretRef})` : "no account"}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">Not configured</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => setEditing(b)} data-testid={`button-cp-configure-${b.id}`}>
                        Configure
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={(data?.page ?? 1) <= 1}>Previous</Button>
        <span className="text-xs text-muted-foreground">Page {data?.page ?? 1} of {data?.totalPages ?? 1}</span>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(data?.totalPages ?? 1, p + 1))} disabled={(data?.page ?? 1) >= (data?.totalPages ?? 1)}>Next</Button>
      </div>
      {editing && <ConfigDialog key={editing.id} brand={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
